import React, { useState, useContext, useEffect, useRef } from 'react';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { useLocalStorage } from '@thd-olt-global/thd-storage-utils';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { AssistantActionButton } from './components/AssistantActionButton';
import { ResponsiveAssistant } from './components/ResponsiveAssistant';
import { IFrameManager } from './components/IFrameManager';
import { AssistantHeader } from './components/AssistantHeader';
import {
  createActionId,
  getDefaultSession,
  isSessionExpired,
} from './utils/assistant-utils';
import { useStickyElementOffset } from './hooks/useStickyElementOffset';
import { MAGIC_APRON_CONTAINER_ID } from './constants/assistant-config';
import './app.scss';
import { MAGIC_APRON_ASSISTANT_ORIGIN } from './constants/assistant-config';

const defaultSession = getDefaultSession();
export const App = () => {
  const { isConsumerApp, customer } = useContext(ExperienceContext);
  const [session, setSession] = useLocalStorage('maa-session', defaultSession);
  const { lesser: isMobile } = useBreakpoint('lg');
  // Do not use this setter directly, use the updateDrawerState instead
  const [drawerState, setDrawerState] = useState('CLOSED');
  const broadcastChannel = useRef(new BroadcastChannel('magic-apron-assistant'));
  const offsetMargin = useStickyElementOffset();

  const fsDTEnabled = useConfigService('magic-apron-assistant-dt-enabled');
  const fsMWEnabled = useConfigService('magic-apron-assistant-mw-enabled');
  const fsAPPEnabled = useConfigService('magic-apron-assistant-app-enabled');

  const updateSession = (sessionUpdates) => setSession({ ...session, ...sessionUpdates });
  const updateDrawerState = (newDrawerState, { skipBroadcast } = {}) => {
    setDrawerState(newDrawerState);
    if (!skipBroadcast) {
      // App opens new tabs instead of reloading current page. This updates drawer state across all app tabs.
      broadcastChannel.current.postMessage({
        type: 'INTERNAL_ONLY_MAA_DRAWER_STATE_CHANGED',
        data: { drawerState: newDrawerState }
      });
    }
  };

  const handleOpen = (isUserInteraction = false) => {
    updateDrawerState('OPEN');
    updateSession({ hasSessionInteractedWithFab: session.hasSessionInteractedWithFab });

    if (isUserInteraction && !session.hasSessionInteractedWithFab) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.fab-click', {
        actionID: createActionId(session.sessionId),
        sessionID: session.sessionId,
      });
    }
  };
  const handleClose = () => {
    updateDrawerState('CLOSED');
    updateSession({ shouldAutoOpen: false });
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('magic-apron-assistant.isReady', { isReady: false });
  };

  useEffect(() => {
    if (isSessionExpired(session.timestamp)) {
      setSession({
        ...defaultSession,
        shouldAutoOpen: session.shouldAutoOpen !== false,
      });
    }

    if (session?.shouldAutoOpen) updateDrawerState(isMobile ? 'MINIMIZED' : 'OPEN');

    // Listen for communication from iframe and cross-tab state synchronization
    if (!broadcastChannel.current) {
      broadcastChannel.current = new BroadcastChannel('magic-apron-assistant');
    }

    broadcastChannel.current.onmessage = ({ data: { type, data } }) => {
      if (type === 'MAA_OPEN_DRAWER') {
        handleOpen(false);
      }
      // TODO Remove this listener after release MAG4-426
      if (type === 'MAA_ASK_A_QUESTION') {
        handleOpen(false);
      }
      if (type === 'MAA_PRODUCT_POD_CLICK') {
        window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.product-pod-click', data);
      }
      if (type === 'MAA_PRODUCT_POD_ATC_CLICK') {
        window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.product-pod-atc-click', data);
      }
      // Handle cross-tab drawer state synchronization for mobile app
      if (type === 'INTERNAL_ONLY_MAA_DRAWER_STATE_CHANGED' && isConsumerApp) {
        updateDrawerState(data.drawerState, { skipBroadcast: true });
      }
    };

    const handleMagicApronEvents = ({ eventName, output }) => {
      if (eventName === 'question-ask') {
        const {
          messageOrigin,
          pageType,
          entryLocationForAnalytics,
          userQuestion,
          sessionID,
          actionID
        } = output;
        updateSession({ shouldAutoOpen: true });
        const question = [
          MAGIC_APRON_ASSISTANT_ORIGIN,
          entryLocationForAnalytics,
          pageType,
          messageOrigin,
          userQuestion
        ].filter(Boolean).join(':');
        window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.submit-click', {
          sessionID,
          question,
          actionID,
        });
      }
    };

    window.LIFE_CYCLE_EVENT_BUS.on('magic-apron-assistant.*', handleMagicApronEvents);

    return () => {
      broadcastChannel.current.close();
      window.LIFE_CYCLE_EVENT_BUS.off('magic-apron-assistant.*', handleMagicApronEvents);
    };
  }, []);

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.drawer-minimize', drawerState);
  }, [drawerState]);

  // On tablets, when changing orientation layout can change between open and minimized
  useEffect(() => {
    if (isMobile && drawerState === 'OPEN') {
      updateDrawerState('MINIMIZED');
    }
    if (!isMobile && drawerState === 'MINIMIZED') {
      updateDrawerState('OPEN');
    }
  }, [isMobile]);

  if (!(
    (fsDTEnabled && !isMobile)
    || (fsMWEnabled && isMobile && !isConsumerApp)
    || (fsAPPEnabled && isMobile && isConsumerApp)
  ) || customer?.type !== 'b2c'
  ) return null;

  return (
    <>
      {(drawerState === 'CLOSED') && (
        <ImpressionProvider
          data={{
            id: MAGIC_APRON_CONTAINER_ID,
            component: 'MagicApronAssistant',
            name: 'MagicApronLoader',
            type: 'content'
          }}
        >
          <AssistantActionButton
            offsetMargin={offsetMargin}
            onClick={() => handleOpen(true)}
          />
        </ImpressionProvider>
      )}
      {(drawerState !== 'CLOSED') && (
        <ResponsiveAssistant
          drawerState={drawerState}
          setDrawerState={(newDrawerState) => {
            if (newDrawerState === 'CLOSED') handleClose();
            if (newDrawerState === 'OPEN') handleOpen();
            if (newDrawerState === 'MINIMIZED') {
              updateDrawerState(newDrawerState);
              updateSession({ shouldAutoOpen: true });
            }
          }}
          offsetMargin={offsetMargin}
        >
          <IFrameManager>
            {drawerState === 'OPEN' && <AssistantHeader onClose={handleClose} />}
          </IFrameManager>
        </ResponsiveAssistant>
      )}
    </>
  );
};
